import type { UseQueryOptions } from '@tanstack/react-query';
import { useApi } from '@/services/queryUtils';
import { createFetcher } from '@/services/createFetcher';

/**
 * Factory for creating a query hook for a standard network-scoped REST endpoint.
 *
 * Handles auth, query key construction, and the enabled guard so each new endpoint
 * only needs to supply its query key prefix and a path builder.
 *
 * @param queryKeyPrefix - Stable string used as the first element of the TanStack Query key.
 * @param buildPath - Receives the resolved networkId and returns the URL path (no query string).
 *   Additional params and date range are appended as query string parameters automatically.
 */
export const createNetworkQuery = <TData, TAdditionalParams = Record<string, unknown>>(
  queryKeyPrefix: string,
  buildPath: (networkId: string) => string
) => {
  return (
    networkId?: string,
    from?: string,
    to?: string,
    options?: Omit<UseQueryOptions<TData, Error>, 'queryKey' | 'queryFn'>,
    additionalParams?: TAdditionalParams
  ) => {
    const enabled = !!networkId && !!from && !!to;
    const queryKey = additionalParams
      ? [queryKeyPrefix, networkId, from, to, ...Object.values(additionalParams)]
      : [queryKeyPrefix, networkId, from, to];

    return useApi<TData>(
      queryKey,
      createFetcher<TData>(networkId ? buildPath(networkId) : '', {
        from,
        to,
        ...(additionalParams ?? {}),
      }),
      { ...options, enabled: options?.enabled ?? enabled }
    );
  };
};
